// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 10-security-compliance.md

import * as React from 'react';

import { getIdpProvider, type IdpProviderDef, type IdpProviderId } from '@/lib/idpProviders';
import { mergeRoleGroup } from '@/lib/roleGroups';
import type { RoleSource } from '@greenlight/shared';

/**
 * Shared OIDC-provider editor state, rendered by both the install wizard
 * (`IdpStep`) and Settings → Identity (`IdentitySettings`). Each surface owns
 * its shell — the wizard's stepper/`installation_state` gating and validation
 * checklist, Settings' reconfigure card and session warnings — and hydrates this
 * hook through a thin adapter, so a field, validation rule, or role-mapping
 * invariant lives in one place instead of drifting between two copies.
 */

export interface IdpRoleGroups {
  org_admin?: string[];
  admin?: string[];
  user?: string[];
}

/** Prefill for the form, adapted from each surface's own persisted shape. */
export interface IdpFormInitial {
  provider: IdpProviderId | null;
  issuerUrl: string;
  clientId: string;
  /** Generic OIDC sign-in button label (`display_name`); empty elsewhere. */
  displayName: string;
  roleSource: RoleSource;
  groupClaim: string;
  /** `role_groups.org_admin[0]` (or a legacy single-field admin id). */
  adminGroupId: string;
  /** `role_groups.admin[0]`. */
  itAdminGroupId: string;
  /** `role_groups.user[0]` — the sign-in restriction. */
  userGroupId: string;
  /** Google `hosted_domains` as the comma-separated string the field edits. */
  hostedDomains: string;
  /** The full stored mapping, carried through a same-provider re-save. */
  roleGroups: IdpRoleGroups | undefined;
}

export interface UseIdpFormConfig {
  initial: IdpFormInitial;
  /**
   * Whether the client secret is mandatory. The wizard always requires one
   * (`() => true`); Settings keeps the stored Key Vault secret only for an edit
   * that stays on the same provider, issuer and client ID, since a secret
   * belongs to one client at one issuer.
   */
  secretRequired: (unchanged: {
    sameProvider: boolean;
    sameIssuer: boolean;
    sameClientId: boolean;
  }) => boolean;
  /** Re-selecting the active provider is a no-op (the wizard, so in-progress
   * fields aren't wiped); Settings re-hydrates the stored provider instead. */
  reselectIsNoop?: boolean;
  /** Selecting the originally-stored provider re-hydrates its saved values
   * (Settings). The wizard clears to provider defaults on every switch. */
  rehydrateStoredOnSelect?: boolean;
  /** Called whenever an edit invalidates a prior validation probe (issuer edit
   * or provider switch) so the shell can reset its own probe/validated state. */
  onInvalidateValidation?: () => void;
}

/** The provider-agnostic OIDC body both surfaces submit. Each shell layers on
 * its surface-only fields (the wizard's `initial_admin_email`). */
export interface IdpFormBody {
  provider: IdpProviderId;
  issuer_url: string;
  client_id: string;
  client_secret?: string;
  display_name?: string;
  role_source: RoleSource;
  group_claim: string;
  role_groups?: IdpRoleGroups;
  hosted_domains?: string[];
}

export interface IdpFormState {
  provider: IdpProviderId | null;
  providerDef: IdpProviderDef | undefined;
  isGoogle: boolean;
  /** Google is server-constrained to Greenlight-managed regardless of the toggle. */
  effectiveRoleSource: RoleSource;
  sameProvider: boolean;
  secretRequired: boolean;
  /** Whether to render the Google Workspace-domain field (optional when shown). */
  showHostedDomains: boolean;
  /** Whether to render the sign-in button label field (generic OIDC). */
  showDisplayName: boolean;
  showRoleSource: boolean;
  showGroupMapping: boolean;

  issuerUrl: string;
  clientId: string;
  clientSecret: string;
  displayName: string;
  roleSource: RoleSource;
  groupClaim: string;
  adminGroupId: string;
  itAdminGroupId: string;
  userGroupId: string;
  hostedDomains: string;

  selectProvider: (id: IdpProviderId) => void;
  setIssuerUrl: (v: string) => void;
  setClientId: (v: string) => void;
  setClientSecret: (v: string) => void;
  setDisplayName: (v: string) => void;
  setRoleSource: (v: RoleSource) => void;
  setGroupClaim: (v: string) => void;
  setAdminGroupId: (v: string) => void;
  setItAdminGroupId: (v: string) => void;
  setUserGroupId: (v: string) => void;
  setHostedDomains: (v: string) => void;

  /** Every field-level save gate except the server-probe `validated` flag and
   * any surface-only gate (the wizard's initial-admin email), which the shell
   * ANDs on. */
  fieldsComplete: boolean;
  /** The `role_groups` to persist, preserving matrix-configured extras. */
  buildRoleGroups: () => IdpRoleGroups | undefined;
  /** The provider-agnostic body; the shell spreads surface-only fields on top. */
  buildBody: () => IdpFormBody;
}

/** The Workspace-domain field edits a comma- or newline-separated list. */
export function splitHostedDomains(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map((d) => d.trim().toLowerCase())
    .filter((d) => d.length > 0);
}

export function joinHostedDomains(domains: string[] | undefined): string {
  return (domains ?? []).join(', ');
}

function trimTrailingSlash(value: string): string {
  return value.trim().replace(/\/+$/, '');
}

export function useIdpForm(config: UseIdpFormConfig): IdpFormState {
  const { initial, secretRequired: secretRequiredFor } = config;

  const [provider, setProvider] = React.useState<IdpProviderId | null>(initial.provider);
  const [issuerUrl, setIssuerUrlState] = React.useState(initial.issuerUrl);
  const [clientId, setClientIdState] = React.useState(initial.clientId);
  const [clientSecret, setClientSecretState] = React.useState('');
  const [roleSource, setRoleSource] = React.useState<RoleSource>(initial.roleSource);
  const [groupClaim, setGroupClaim] = React.useState(initial.groupClaim);
  const [adminGroupId, setAdminGroupId] = React.useState(initial.adminGroupId);
  const [itAdminGroupId, setItAdminGroupId] = React.useState(initial.itAdminGroupId);
  const [userGroupId, setUserGroupId] = React.useState(initial.userGroupId);
  const [hostedDomains, setHostedDomains] = React.useState(initial.hostedDomains);
  const [displayName, setDisplayName] = React.useState(initial.displayName);

  const providerDef = getIdpProvider(provider ?? undefined);
  const isGoogle = provider === 'google';
  const showHostedDomains = isGoogle;
  const showDisplayName = Boolean(providerDef?.collectsDisplayName);
  const effectiveRoleSource: RoleSource = isGoogle ? 'greenlight' : roleSource;
  const sameProvider = provider !== null && provider === initial.provider;
  const sameIssuer = trimTrailingSlash(issuerUrl) === trimTrailingSlash(initial.issuerUrl);
  const sameClientId = clientId.trim() === initial.clientId.trim();
  const secretRequired = secretRequiredFor({ sameProvider, sameIssuer, sameClientId });
  const showRoleSource = provider !== null && !isGoogle;
  const showGroupMapping = !isGoogle && effectiveRoleSource === 'idp';

  const invalidate = config.onInvalidateValidation;

  // Verification covers the issuer, the client ID and the secret together, so
  // editing any of them retires the prior result — a green check from an
  // earlier client ID must never gate a save of a new one.
  const setIssuerUrl = (v: string): void => {
    setIssuerUrlState(v);
    invalidate?.();
  };

  const setClientId = (v: string): void => {
    setClientIdState(v);
    invalidate?.();
  };

  const setClientSecret = (v: string): void => {
    setClientSecretState(v);
    invalidate?.();
  };

  const selectProvider = (id: IdpProviderId): void => {
    if (config.reselectIsNoop && id === provider) return;
    setProvider(id);
    const def = getIdpProvider(id);
    const wasStored = id === initial.provider;
    const rehydrate = Boolean(config.rehydrateStoredOnSelect) && wasStored;
    // Switching providers clears every OIDC field — group identifiers and
    // secrets are provider-specific and must not survive a swap. Re-selecting
    // the originally-stored provider (Settings only) restores its saved values.
    setIssuerUrl(rehydrate ? initial.issuerUrl : (def?.defaultIssuer ?? ''));
    setClientId(rehydrate ? initial.clientId : '');
    setClientSecret('');
    setRoleSource(
      id === 'google'
        ? 'greenlight'
        : rehydrate
          ? initial.roleSource
          : (def?.defaultRoleSource ?? 'idp'),
    );
    setDisplayName(rehydrate ? initial.displayName : '');
    setGroupClaim(rehydrate ? initial.groupClaim : (def?.defaultGroupClaim ?? 'groups'));
    setAdminGroupId(rehydrate ? initial.adminGroupId : '');
    setItAdminGroupId(rehydrate ? initial.itAdminGroupId : '');
    setUserGroupId(rehydrate ? initial.userGroupId : '');
    setHostedDomains(rehydrate ? initial.hostedDomains : '');
    invalidate?.();
  };

  const buildRoleGroups = (): IdpRoleGroups | undefined => {
    // Google carries no mapping — its roles are dashboard-managed.
    if (isGoogle) return undefined;
    const preserved = sameProvider ? initial.roleGroups : undefined;
    const user = mergeRoleGroup(userGroupId, preserved?.user);
    if (effectiveRoleSource === 'idp') {
      const orgAdmin = mergeRoleGroup(adminGroupId, preserved?.org_admin);
      const admin = mergeRoleGroup(itAdminGroupId, preserved?.admin);
      const rg: IdpRoleGroups = {
        ...(orgAdmin.length ? { org_admin: orgAdmin } : {}),
        ...(admin.length ? { admin } : {}),
        ...(user.length ? { user } : {}),
      };
      return Object.keys(rg).length > 0 ? rg : undefined;
    }
    // Greenlight-managed: the org_admin/admin mapping is inert but round-trips
    // (so a later switch back to `idp` restores it); the user sign-in
    // restriction is orthogonal to role source and stays editable (docs/10).
    const rg: IdpRoleGroups = {
      ...(preserved?.org_admin?.length ? { org_admin: preserved.org_admin } : {}),
      ...(preserved?.admin?.length ? { admin: preserved.admin } : {}),
      ...(user.length ? { user } : {}),
    };
    return Object.keys(rg).length > 0 ? rg : undefined;
  };

  const buildBody = (): IdpFormBody => {
    const roleGroups = buildRoleGroups();
    return {
      provider: provider as IdpProviderId,
      issuer_url: issuerUrl.trim(),
      client_id: clientId.trim(),
      role_source: effectiveRoleSource,
      group_claim: groupClaim.trim() || 'groups',
      ...(secretRequired || clientSecret.trim() ? { client_secret: clientSecret } : {}),
      ...(showDisplayName && displayName.trim() ? { display_name: displayName.trim() } : {}),
      ...(roleGroups ? { role_groups: roleGroups } : {}),
      // Sent for Google even when empty — clearing the list is a real edit, and
      // an omitted key would leave the previous restriction in place.
      ...(isGoogle ? { hosted_domains: splitHostedDomains(hostedDomains) } : {}),
    };
  };

  const fieldsComplete =
    provider !== null &&
    clientId.trim() !== '' &&
    (!secretRequired || clientSecret.trim() !== '') &&
    (effectiveRoleSource === 'idp' ? adminGroupId.trim() !== '' : true);

  return {
    provider,
    providerDef,
    isGoogle,
    effectiveRoleSource,
    sameProvider,
    secretRequired,
    showHostedDomains,
    showDisplayName,
    showRoleSource,
    showGroupMapping,
    issuerUrl,
    clientId,
    clientSecret,
    displayName,
    roleSource,
    groupClaim,
    adminGroupId,
    itAdminGroupId,
    userGroupId,
    hostedDomains,
    selectProvider,
    setIssuerUrl,
    setClientId,
    setClientSecret,
    setDisplayName,
    setRoleSource,
    setGroupClaim,
    setAdminGroupId,
    setItAdminGroupId,
    setUserGroupId,
    setHostedDomains,
    fieldsComplete,
    buildRoleGroups,
    buildBody,
  };
}
